import styled, { css } from 'styled-components';
import { Input } from '@shared/ui/Input';
import { fleetLeading, fleetType } from '@entities/fleet';

/**
 * Числовая таблица ПРЦ: подписи колонок мелкими прописными, числа справа
 * моноширинными цифрами, первая колонка липкая — при прокрутке вбок видно,
 * какая это строка.
 */
export const NumTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};

  thead th {
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderStrong};
    font-size: ${fleetType.micro};
    font-weight: 600;
    letter-spacing: 0.04em;
    line-height: ${fleetLeading.tight};
    text-transform: uppercase;
    text-align: left;
    color: ${({ theme }) => theme.colors.text.secondary};
    vertical-align: bottom;
    text-wrap: balance;
  }

  thead th.num {
    text-align: right;
  }

  td,
  tbody th {
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
    text-align: left;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.background};
    transition: background ${({ theme }) => theme.motion.fast}
      ${({ theme }) => theme.motion.easeOut};
  }

  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  td.strong,
  th.strong {
    font-weight: 600;
  }

  td.muted {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  .sticky {
    position: sticky;
    left: 0;
    z-index: 1;
  }

  thead th.sticky {
    background: ${({ theme }) => theme.colors.background};
    z-index: 2;
  }

  tr.group > th {
    padding: ${({ theme }) => `${theme.spacing(5)} ${theme.spacing(2.5)} ${theme.spacing(2)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderStrong};
    background: ${({ theme }) => theme.colors.background};
    font-size: ${fleetType.small};
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  tr.group > th > .meta {
    margin-left: ${({ theme }) => theme.spacing(2)};
    font-weight: 400;
    letter-spacing: normal;
    text-transform: none;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  tr.row:hover > td,
  tr.row:hover > th,
  tr.row:focus-within > td,
  tr.row:focus-within > th {
    background: ${({ theme }) => theme.colors.surface};
  }

  tr.total > td,
  tr.total > th {
    border-top: 1px solid ${({ theme }) => theme.colors.borderStrong};
    font-weight: 600;
  }
`;

/** Таблица по годам из одной строки полей — по содержимому, а не во всю ширину. */
export const CompactTable = styled(NumTable)`
  width: auto;
  min-width: min(560px, 100%);

  td {
    min-width: 104px;
  }
`;

/**
 * Строки таблиц с полями ввода: текстовые ячейки выровнены по тексту поля
 * (поле выше строки текста), чтобы подпись и число стояли на одной линии.
 */
export const editorRows = css`
  tbody th,
  td.text {
    padding-top: ${({ theme }) => theme.spacing(3.5)};
  }
`;

/** Поле ввода в ячейке таблицы: компактное, число справа. */
export const GridInput = styled(Input)`
  min-width: 96px;
  height: 32px;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  font-size: ${fleetType.body};
  text-align: right;
  font-variant-numeric: tabular-nums;

  /* Подсказка-значение по индексу — читаемая, но тише введённого. */
  &::placeholder {
    color: ${({ theme }) => theme.colors.text.secondary};
    opacity: 1;
  }

  &[aria-invalid='true'] {
    border-color: ${({ theme }) => theme.colors.status.danger};
  }

  /* Правленая ячейка — синей кромкой: красная у ошибки ввода. */
  &[data-changed='true'] {
    border-color: ${({ theme }) => theme.colors.status.info};
    background: ${({ theme }) => theme.colors.status.info}0F;
  }

  &[data-conflict='true'] {
    border-color: ${({ theme }) => theme.colors.status.warning};
  }
`;

/** Пометка «вручную» под полем ручной цены года. */
export const ManualMark = styled.span`
  display: block;
  margin-top: 2px;
  font-size: ${fleetType.micro};
  font-weight: 600;
  line-height: ${fleetLeading.tight};
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: right;
  white-space: nowrap;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    margin-right: ${({ theme }) => theme.spacing(1)};
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    vertical-align: 1px;
  }
`;
